import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Pagination,
  Chip,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Refresh,
  FileUpload,
  Info,
  ExpandMore,
  GetApp,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface CsvUpload {
  id: number;
  filename: string;
  file_size: number;
  total_contacts: number;  // Accepted contacts count
  processed_contacts: number;
  csv_rows_count: number;  // Total rows in CSV
  rejected_contacts: number;  // Rejected contacts count
  upload_timestamp: string;
  description?: string;
  tags?: string[];
  is_active: boolean;
}

interface CsvUploadListResponse {
  uploads: CsvUpload[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const CsvUploadsPage: React.FC = () => {
  const [uploads, setUploads] = useState<CsvUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchUploads = async (pageNum: number = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/csv-uploads/?page=${pageNum}&page_size=25`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch uploads');
      }

      const data: CsvUploadListResponse = await response.json();
      setUploads(data.uploads);
      setTotalPages(data.total_pages);
      setTotal(data.total);
      setPage(data.page);
    } catch (error) {
      console.error('Error fetching uploads:', error);
      setSnackbar({ open: true, message: 'Failed to fetch uploads', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUploads();
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setSnackbar({ open: true, message: 'Please select a CSV file', severity: 'error' });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (description) formData.append('description', description);
      if (tags) formData.append('tags', JSON.stringify(tags.split(',').map(t => t.trim())));

      const response = await fetch('/api/v1/csv-uploads/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      const statusMessage = `Upload completed! Total Contacts: ${responseData.csv_rows_count}, Accepted: ${responseData.total_contacts}, Rejected: ${responseData.rejected_contacts}`;
      setSnackbar({ open: true, message: statusMessage, severity: 'success' });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setDescription('');
      setTags('');
      fetchUploads();
    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({ open: true, message: error instanceof Error ? error.message : 'Upload failed', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };


  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/csv-uploads/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      setSnackbar({ open: true, message: 'File deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeleteId(null);
      fetchUploads(page);
    } catch (error) {
      console.error('Delete error:', error);
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchUploads(value);
  };

  const handleDownloadSample = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/csv-uploads/sample', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to download sample CSV');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sample_contacts.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Sample download error:', error);
      setSnackbar({ open: true, message: 'Failed to download sample CSV', severity: 'error' });
    }
  };


  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            CSV Uploads
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your contact CSV files for automated calling
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleDownloadSample}
            size="large"
          >
            Download Sample
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
            size="large"
          >
            Upload CSV
          </Button>
        </Box>
      </Box>

      {/* Instructions Accordion */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center">
            <Info sx={{ mr: 1 }} />
            <Typography variant="h6">
              CSV Format Requirements
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            <strong>Required columns:</strong> phone (only phone number is required)
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Optional columns:</strong> name, email, company, title, city, state, notes, or any other custom fields
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Phone number format:</strong> Must start with '+' and include country code (e.g., +17066806272)
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>File requirements:</strong>
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>File must be in CSV format (.csv extension)</li>
            <li>File must be UTF-8 encoded</li>
            <li>Maximum file size: 10MB</li>
            <li>Maximum contacts: 10,000</li>
            <li>Only phone field is required, all other fields are optional</li>
            <li>Phone numbers must start with '+' and contain country code</li>
            <li>Only contacts with valid phone numbers (starting with '+') will be accepted</li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Tip:</strong> Download the sample CSV file above to see the correct format and structure.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Uploaded Files ({total})
            </Typography>
            <IconButton onClick={() => fetchUploads(page)} disabled={loading}>
              <Refresh />
            </IconButton>
          </Box>

          {loading && <LinearProgress />}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Upload #</TableCell>
                  <TableCell>Filename</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>CSV Rows</TableCell>
                  <TableCell>Accepted</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploads.map((upload) => (
                  <TableRow key={upload.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        #{upload.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <FileUpload sx={{ mr: 1, color: 'text.secondary' }} />
                        {upload.filename}
                      </Box>
                    </TableCell>
                    <TableCell>{formatFileSize(upload.file_size)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {upload.csv_rows_count} rows
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {upload.total_contacts} accepted
                        </Typography>
                        {upload.rejected_contacts > 0 && (
                          <Typography variant="caption" color="error">
                            {upload.rejected_contacts} rejected
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {format(new Date(upload.upload_timestamp), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {upload.description || '-'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {upload.tags?.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteId(upload.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload CSV File</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Required columns:</strong> phone (only phone number is required)<br/>
              <strong>Phone format:</strong> Must start with '+' and include country code (e.g., +17066806272)<br/>
              <strong>Optional columns:</strong> name, email, company, title, city, state, notes, or any custom fields<br/>
              <strong>Max file size:</strong> 10MB | <strong>Max contacts:</strong> 10,000
            </Typography>
          </Alert>
          
          <Box mb={2}>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="csv-file-input"
            />
            <label htmlFor="csv-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ py: 2 }}
              >
                {selectedFile ? selectedFile.name : 'Select CSV File'}
              </Button>
            </label>
          </Box>
          
          <TextField
            fullWidth
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />
          
          <TextField
            fullWidth
            label="Tags (comma-separated, optional)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            margin="normal"
            placeholder="e.g., leads, prospects, customers"
          />

          {selectedFile && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Selected file: {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete CSV Upload</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this CSV upload? This action cannot be undone.
            All associated contacts will also be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
